import MoreVertIcon from '@mui/icons-material/MoreVert'
import { IconButton, Menu, MenuItem } from '@mui/material'
import { CargoType, CarrierType, CarrierVisitDirection } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { OrderNavigationDirection, useOrderNavigation } from '@planning/hooks/useOrderNavigation'
import { useVesselVisitBreadcrumbs } from '@planning/hooks/useVesselVisitBreadcrumbs'
import { CreateGeneralCargoOrder } from '@planning/pages/Order/CreateGeneralCargoOrders'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { orderListParsingService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Header } from '@tom-ui/ui'
import { downloadFile } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import Papa from 'papaparse'
import { FC, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { VesselVisitOrderUploadButton } from '../atoms/VesselVisitOrderUploadButton'

type Props = {
  vesselVisitItem: IVesselVisitItem
  direction: OrderNavigationDirection
  onUploadClick: () => void
}

export const VesselVisitOrderListHeader: FC<Props> = observer(
  ({ vesselVisitItem, direction, onUploadClick }) => {
    const { t } = useTranslate()
    const { navigateVisitOrderPage } = useOrderNavigation()
    const { breadcrumbs } = useVesselVisitBreadcrumbs(vesselVisitItem)
    const { dialogStore, generalCargoViewStore } = usePlanningStore()

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
      setAnchorEl(null)
    }

    const vessel = computed(() => vesselVisitItem.vessels?.[0]?.data).get()

    const directionLabel = useMemo(() => {
      switch (direction) {
        case 'load':
          return t('loadList', 'Load List')
        case 'discharge':
          return t('dischargeList', 'Discharge List')
        case 'restow':
          return t('restowList', 'Restow List')
      }
    }, [direction, t])

    const title = useMemo(
      () =>
        vessel ? `${vessel.name} <${vessel.shippingLine}> - ${directionLabel}` : directionLabel,
      [vessel, directionLabel],
    )

    const carrierDirection =
      direction === 'discharge' ? CarrierVisitDirection.Inbound : CarrierVisitDirection.Outbound

    const handleExportOrders = () => {
      const { fileName, header, data } = orderListParsingService.parseOrderListExport(
        CarrierType.Vessel,
        carrierDirection,
        vesselVisitItem,
      )

      const csvData = Papa.unparse({
        fields: header,
        data: data,
      })

      downloadFile(new Blob([csvData as BlobPart]), fileName)
    }

    const handleAddOrder = async () => {
      const visitId = vesselVisitItem?.id
      if (!visitId) return

      if (isContainerVisit) {
        navigateVisitOrderPage({
          carrierVisitId: visitId,
          carrierVisitType: CarrierType.Vessel,
          direction: direction,
        })
      } else {
        await generalCargoViewStore.getPackages()

        dialogStore.openDialog(
          <CreateGeneralCargoOrder
            carrierVisit={vesselVisitItem.data}
            carrierVisitType={CarrierType.Vessel}
            forcedDirection={carrierDirection}
            disableCarrierVisitSelection
          />,
          true,
          {
            open: true,
            maxWidth: 'lg',
          },
        )
      }
    }

    const isContainerVisit = vesselVisitItem?.data.cargoType === CargoType.Container
    const isGeneralCargoVisit = vesselVisitItem?.data.cargoType === CargoType.GeneralCargo

    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <Breadcrumbs items={breadcrumbs} />
        <Header
          title={directionLabel}
          position='relative'
          rightRenderOption={
            <>
              <VesselVisitOrderUploadButton
                vesselVisitItem={vesselVisitItem}
                onClick={onUploadClick}
              />

              <IconButton onClick={handleMenuOpen} data-cy='visit-orders-options-btn'>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem
                  onClick={handleAddOrder}
                  data-cy='visit-add-order-btn'
                  disabled={vesselVisitItem.isVesselVisitBlockedForEdit}
                >
                  {direction === 'restow'
                    ? t('addRestowOrder', 'Add restow order')
                    : t('addOrder', 'Add order')}
                </MenuItem>
                {direction !== 'restow' && !isGeneralCargoVisit && (
                  <MenuItem onClick={handleExportOrders}>
                    {t('exportOrders', 'Export orders')}
                  </MenuItem>
                )}
              </Menu>
            </>
          }
        />
      </>
    )
  },
)
